/**
 * Chart view — Chart.js cumulative timeline for the portfolio.
 *
 * Consumes the `TimelinePoint[]` produced by `TimelineBuilder` (task 5.2)
 * and renders a multi-series line chart with:
 *
 *   1. Seven toggleable series, all visible on first render:
 *        `maturity`, `prod`, `dev`, `pipe`, `revenue`, `adoption`, `tasks`
 *   2. A non-toggleable overlay of per-point markers, one per
 *      `TimelinePoint`, keyed to `caseId`. Clicking a marker emits exactly
 *      one `select(caseId)` event.
 *   3. An empty-state placeholder that renders when the input array is
 *      empty; Chart.js is not instantiated in that case (Requirement 12.2).
 *
 * DOM structure mirrors the prototype (`ai-maturity-index-portal_9.html`):
 *
 *   <section class="chart">
 *     <div class="chart-toggles" role="group" aria-label="Chart series">
 *       <label class="chart-toggle">
 *         <input type="checkbox" data-series="maturity" checked />
 *         <span class="chart-toggle-swatch" …></span>
 *         <span class="chart-toggle-label">Maturity</span>
 *       </label>
 *       …
 *     </div>
 *     <div class="chart-body">
 *       <canvas class="chart-canvas"></canvas>
 *       <div class="chart-empty" hidden>No use cases yet</div>
 *     </div>
 *   </section>
 *
 * The two large-magnitude series (`revenue`, `adoption`, `tasks`) render on
 * a secondary right-hand y-axis so they don't visually flatten the small
 * count series (`maturity`, `prod`, `dev`, `pipe`) on the primary axis.
 *
 * Toggle state is preserved across `Case[]` changes: `render()` updates the
 * existing Chart instance's data in place (Requirement 12.9) rather than
 * tearing down and re-creating, so per-series visibility survives.
 *
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5, 12.6, 12.7, 12.8, 12.9.
 */
import Chart, { type ActiveElement, type ChartEvent } from "chart.js/auto";
import type { TimelinePoint } from "@aim/types";

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/** Public surface returned by `createChartView`. */
export interface ChartView {
  /**
   * Render (or re-render) the chart from `points`. When `points` is empty,
   * the empty-state placeholder is shown and any live Chart.js instance is
   * torn down (Requirement 12.2). When non-empty, the existing Chart.js
   * instance is updated in place — series visibility is preserved
   * (Requirement 12.9).
   */
  render(points: readonly TimelinePoint[]): void;

  /**
   * Register a listener invoked with a `caseId` each time the analyst
   * clicks or taps an overlay marker (Requirement 12.7). Calling
   * `onSelect` a second time replaces the previous listener — the view
   * emits exactly one selection per click regardless of how many
   * datasets overlap that pixel (Requirement 12.8).
   */
  onSelect(cb: (caseId: string) => void): void;

  /**
   * Tear down the chart (if any) and remove the view's DOM shell. Safe to
   * call more than once.
   */
  destroy(): void;
}

// ---------------------------------------------------------------------------
// Series definition
// ---------------------------------------------------------------------------

/** The seven toggleable series keys, in fixed render order. */
export type SeriesKey =
  | "maturity"
  | "prod"
  | "dev"
  | "pipe"
  | "revenue"
  | "adoption"
  | "tasks";

interface SeriesDef {
  /** Field on `TimelinePoint`; identical to the DOM `data-series` attribute. */
  readonly key: SeriesKey;
  /** Human-readable label for the toggle control. */
  readonly label: string;
  /**
   * Which y-axis to bind to. `y` is the primary (left) axis, sized for
   * small count series; `y1` is the secondary (right) axis, sized for
   * revenue/adoption/tasks totals.
   */
  readonly axis: "y" | "y1";
  /** Stroke color for the line and its point markers. */
  readonly color: string;
}

/**
 * Fixed order of the seven series. The order is also the DOM order of the
 * toggle controls and the Chart.js dataset index for each series (index
 * `SERIES.length` is reserved for the overlay dataset).
 *
 * Colors are drawn from the Tableau 10 palette to stay legible on both
 * light and dark chart backgrounds without depending on any CSS variables.
 */
const SERIES: readonly SeriesDef[] = [
  { key: "maturity", label: "Maturity", axis: "y", color: "#4c78a8" },
  { key: "prod", label: "In production", axis: "y", color: "#54a24b" },
  { key: "dev", label: "In development", axis: "y", color: "#f58518" },
  { key: "pipe", label: "In pipeline", axis: "y", color: "#eeca3b" },
  { key: "revenue", label: "Revenue", axis: "y1", color: "#e45756" },
  { key: "adoption", label: "Adoption", axis: "y1", color: "#72b7b2" },
  { key: "tasks", label: "AI tasks", axis: "y1", color: "#b279a2" },
];

/** Dataset index reserved for the always-visible marker overlay. */
const OVERLAY_INDEX = SERIES.length;

/** Copy shown when the portfolio is empty. */
const EMPTY_MESSAGE = "No use cases yet";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Shape stored in each Chart.js data point. Chart.js keeps arbitrary extra
 * properties intact on parsed data, so we thread `caseId` through here to
 * resolve clicks back to a portfolio entry.
 */
interface ChartPointDatum {
  x: number;
  y: number;
  caseId: string;
}

/** Build a point array for a specific series from the timeline. */
function pointsForSeries(
  timeline: readonly TimelinePoint[],
  key: SeriesKey,
): ChartPointDatum[] {
  return timeline.map((p) => ({ x: p.day, y: p[key], caseId: p.caseId }));
}

/**
 * Build the overlay data — one marker per `TimelinePoint`. The overlay
 * anchors visually to `maturity` (a canonical "landing" indicator) so
 * markers align with the primary index line when it is visible.
 */
function overlayPoints(timeline: readonly TimelinePoint[]): ChartPointDatum[] {
  return timeline.map((p) => ({ x: p.day, y: p.maturity, caseId: p.caseId }));
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a chart view bound to `container`. The view mounts its own DOM
 * shell into `container` immediately, replacing any prior contents so
 * calling this factory twice on the same node is safe.
 */
export function createChartView(container: HTMLElement): ChartView {
  // ------------------------------------------------------------------ DOM shell
  container.replaceChildren();

  const root = document.createElement("section");
  root.className = "chart";

  const toggles = document.createElement("div");
  toggles.className = "chart-toggles";
  toggles.setAttribute("role", "group");
  toggles.setAttribute("aria-label", "Chart series");

  const body = document.createElement("div");
  body.className = "chart-body";

  const canvas = document.createElement("canvas");
  canvas.className = "chart-canvas";

  const empty = document.createElement("div");
  empty.className = "chart-empty";
  empty.textContent = EMPTY_MESSAGE;
  empty.hidden = true;

  body.append(canvas, empty);
  root.append(toggles, body);
  container.append(root);

  // ------------------------------------------------------------------ Toggle UI
  // The set records the hidden series so re-renders (Requirement 12.9)
  // preserve visibility without a Chart.js re-mount.
  const hidden = new Set<SeriesKey>();

  const toggleInputs = new Map<SeriesKey, HTMLInputElement>();
  for (const s of SERIES) {
    const label = document.createElement("label");
    label.className = "chart-toggle";

    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = true; // Requirement 12.3: all visible on first render.
    input.dataset["series"] = s.key;

    const swatch = document.createElement("span");
    swatch.className = "chart-toggle-swatch";
    swatch.style.backgroundColor = s.color;

    const text = document.createElement("span");
    text.className = "chart-toggle-label";
    text.textContent = s.label;

    input.addEventListener("change", () => {
      if (input.checked) {
        hidden.delete(s.key);
      } else {
        hidden.add(s.key);
      }
      applyToggleToChart(s.key, input.checked);
    });

    label.append(input, swatch, text);
    toggles.append(label);
    toggleInputs.set(s.key, input);
  }

  // ------------------------------------------------------------------ State
  // `chart` is null until a non-empty render creates it. `selectCb` is
  // replaced by each `onSelect` call — we always emit through the most
  // recently registered listener (Requirement 12.7: "exactly one" event).
  let chart: Chart | null = null;
  let currentPoints: readonly TimelinePoint[] = [];
  let selectCb: ((caseId: string) => void) | null = null;

  /** Apply the current hidden-set entry for `key` to the live chart. */
  function applyToggleToChart(key: SeriesKey, visible: boolean): void {
    if (chart === null) return;
    const idx = SERIES.findIndex((s) => s.key === key);
    if (idx < 0) return;
    chart.setDatasetVisibility(idx, visible);
    chart.update("none");
  }

  /**
   * Emit exactly one `select(caseId)` per click, using the top-most
   * marker under the pointer as the source (Requirement 12.8). All
   * datasets share the same `x` per data index, so any hit at index `i`
   * resolves to the same `caseId` (`currentPoints[i].caseId`).
   */
  function handleClick(
    event: ChartEvent,
    _elements: ActiveElement[],
    ch: Chart,
  ): void {
    if (selectCb === null) return;
    // `getElementsAtEventForMode` accepts a DOM Event; `ChartEvent`
    // wraps one in `native`. Fall back to the wrapper when the native
    // event is unavailable (e.g. programmatic clicks in tests).
    const nativeEvent = event.native ?? (event as unknown as Event);
    const hits = ch.getElementsAtEventForMode(
      nativeEvent,
      "nearest",
      { intersect: true },
      false,
    ) as ReadonlyArray<{ datasetIndex: number; index: number }>;
    if (hits.length === 0) return;

    // Prefer the overlay dataset when it was among the hit elements —
    // it's rendered on top of the series lines, so this matches
    // "topmost marker in render order" for overlapping stacks.
    const overlayHit = hits.find((h) => h.datasetIndex === OVERLAY_INDEX);
    const chosen = overlayHit ?? hits[0];
    if (chosen === undefined) return;

    const point = currentPoints[chosen.index];
    if (point === undefined) return;

    selectCb(point.caseId);
  }

  /** Create the Chart.js instance for a non-empty timeline. */
  function createChart(points: readonly TimelinePoint[]): Chart {
    const datasets = SERIES.map((s) => ({
      label: s.label,
      data: pointsForSeries(points, s.key),
      borderColor: s.color,
      backgroundColor: s.color,
      yAxisID: s.axis,
      tension: 0.15,
      pointRadius: 3,
      pointHoverRadius: 5,
      hidden: hidden.has(s.key),
      // Chart.js Legend is disabled in favour of our custom toggles, so
      // there's no built-in-legend interaction to worry about.
    }));

    // Overlay dataset — one marker per TimelinePoint keyed to caseId.
    // `showLine: false` renders points only; `order: -1` draws it on top
    // of the series lines so it satisfies "overlay" both semantically
    // and visually (Requirement 12.6).
    const overlay = {
      label: "__overlay__",
      data: overlayPoints(points),
      borderColor: "rgba(31, 41, 55, 0.9)",
      backgroundColor: "rgba(31, 41, 55, 0.9)",
      yAxisID: "y",
      showLine: false,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointStyle: "circle" as const,
      order: -1,
    };

    return new Chart(canvas, {
      type: "line",
      data: {
        // All datasets share the same set of x values (one per
        // TimelinePoint), so we don't set top-level labels here.
        datasets: [...datasets, overlay],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        parsing: false,
        interaction: { mode: "nearest", intersect: true },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const first = items[0];
                if (first === undefined) return "";
                const idx = first.dataIndex;
                const p = currentPoints[idx];
                return p !== undefined ? p.dateISO : "";
              },
            },
            filter: (item) => item.datasetIndex !== OVERLAY_INDEX,
          },
        },
        scales: {
          x: {
            type: "linear",
            title: { display: true, text: "Day" },
          },
          y: {
            type: "linear",
            position: "left",
            beginAtZero: true,
            title: { display: true, text: "Count" },
          },
          y1: {
            type: "linear",
            position: "right",
            beginAtZero: true,
            grid: { drawOnChartArea: false },
            title: { display: true, text: "Revenue / adoption / tasks" },
          },
        },
        onClick: handleClick,
      },
    });
  }

  /** Rebuild all dataset arrays in place from `points`. */
  function refreshDatasets(points: readonly TimelinePoint[]): void {
    if (chart === null) return;
    for (let i = 0; i < SERIES.length; i += 1) {
      const s = SERIES[i];
      if (s === undefined) continue;
      const ds = chart.data.datasets[i];
      if (ds === undefined) continue;
      ds.data = pointsForSeries(points, s.key);
    }
    const overlayDs = chart.data.datasets[OVERLAY_INDEX];
    if (overlayDs !== undefined) {
      overlayDs.data = overlayPoints(points);
    }
    chart.update("none");
  }

  // ------------------------------------------------------------------ Return
  return {
    render(points: readonly TimelinePoint[]): void {
      currentPoints = points;

      if (points.length === 0) {
        // Requirement 12.2: empty portfolio → placeholder, no Chart.js.
        if (chart !== null) {
          chart.destroy();
          chart = null;
        }
        canvas.hidden = true;
        empty.hidden = false;
        return;
      }

      canvas.hidden = false;
      empty.hidden = true;

      if (chart === null) {
        chart = createChart(points);
      } else {
        // Requirement 12.9: preserve toggle state across re-renders.
        refreshDatasets(points);
      }
    },

    onSelect(cb: (caseId: string) => void): void {
      selectCb = cb;
    },

    destroy(): void {
      if (chart !== null) {
        chart.destroy();
        chart = null;
      }
      selectCb = null;
      root.remove();
    },
  };
}
